import React, { useState, useEffect } from 'react';
import ChatbotList from './ChatbotList';
import { useChatbotContext } from '../../../../context/ChatbotContext';
import { useUser } from '../../../../context/UserContext';
import logger from '../../../../utils/logger';
import CreateChatbotForm from './CreateChatbotForm';
import { Plus, Search, LayoutGrid, List as ListIcon } from 'lucide-react';

function ChatbotSection({ onEditChatbot }) {
  const { chatbots, loading } = useChatbotContext();
  const { user } = useUser();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');

  // Auto-open if no chatbots exist
  useEffect(() => {
    if (!loading && chatbots.length === 0) {
      setIsCreateOpen(true);
    }
  }, [chatbots, loading]);

  const handleDelete = async (chatbotId) => {
    if (!window.confirm('Are you sure you want to delete this chatbot?')) return;
    try {
      const response = await fetch(`/api/chatbot?chatbotId=${chatbotId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete chatbot');
    } catch (error) {
      logger.error('Error deleting chatbot:', error);
    }
  };

  const filteredChatbots = chatbots.filter(bot =>
    (bot.config?.assistantName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    bot.chatbot_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Mock stats for now - to be connected to backend later
  // const stats = [
  //   { label: 'Active Chatbots', value: chatbots.length, icon: Zap, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  //   { label: 'Total Messages', value: '2.4k', icon: MessageSquare, color: 'text-violet-500', bg: 'bg-violet-500/10' },
  //   { label: 'Response Rate', value: '98%', icon: Activity, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  // ];

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50/50">
      {/* Header & Stats Section */}
      <div className="px-8 pt-8 pb-6 flex flex-col gap-6 border-b border-border/40 bg-white/40 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex justify-between items-end">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
              Good afternoon, {user?.displayName ? user.displayName.split(' ')[0] : 'Creator'}
            </h1>
            <p className="text-slate-500 mt-1 text-sm font-medium">Here's what's happening with your assistants today.</p>
          </div>

          <button
            onClick={() => setIsCreateOpen(true)}
            className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-xl font-medium transition-all shadow-lg shadow-slate-900/10 hover:shadow-slate-900/20 active:scale-95"
          >
            <Plus size={18} />
            <span>New Chatbot</span>
          </button>
        </div>

        {/* Quick Stats Row */}
        {/* <div className="grid grid-cols-3 gap-4">
          {stats.map((stat, i) => (
            <div key={i} className="bg-white/60 border border-white/60 shadow-sm rounded-xl p-4 flex items-center gap-4 transition-all hover:bg-white/80">
              <div className={`p-3 rounded-lg ${stat.bg} ${stat.color}`}>
                <stat.icon size={20} />
              </div>
              <div>
                <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">{stat.label}</p>
                <p className="text-xl font-bold text-slate-800">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>*/}

        {/* Controls Bar */}
        <div className="flex items-center gap-4 mt-2">
          <div className="relative flex-1 group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-violet-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Search chatbots..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:border-violet-500/50 focus:ring-4 focus:ring-violet-500/5 transition-all shadow-sm group-hover:border-slate-300"
            />
          </div>

          <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <ListIcon size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Content Area - Scrollable */}
      <div className="flex-1 overflow-y-auto p-8">
        <ChatbotList
          chatbots={filteredChatbots}
          onDelete={handleDelete}
          onEditChatbot={onEditChatbot}
          viewMode={viewMode}
          onCreate={() => setIsCreateOpen(true)}
        />
      </div>

      <CreateChatbotForm
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        createdCallback={() => {
          logger.log('Chatbot created successfully');
        }}
      />
    </div>
  );
}

export default ChatbotSection;